import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Cable, Cloud, FileBarChart, Radio, TriangleAlert } from 'lucide-react';
import type { AuditEntry, Olt, Onu, Page, Report, SystemInfo } from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatRelative } from '../lib/format';
import { useSession } from '../lib/session';
import { ServerResourcesCard } from './dashboard/ServerResourcesCard';
import {
  Card,
  EmptyState,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/**
 * The operator's landing page: what the platform is running right now, and
 * anything asking for attention. Every panel is capability-gated, so a viewer
 * sees a smaller board rather than a wall of empty cards.
 */
export function DashboardPage() {
  const { session, activeOrg, can } = useSession();
  // Dismissals live for the session only: a warning the operator has read
  // should not nag, but it must come back if it is still true tomorrow.
  const canReadFleet = can('olt.read');
  const canReadAudit = can('audit.read');
  const canReadSettings = can('settings.manage');
  /**
   * Superadmin only, and never grantable to an org role — the server is the one
   * thing on this page that belongs to nobody's organization.
   *
   * It takes the OLT fleet card's slot rather than joining it: the fleet's state
   * is already in the four stat cards above (OLTs up, ONUs online, unauthorised),
   * so a superadmin loses the per-OLT table, not the fleet. An org user's board
   * is untouched.
   */
  const canManageInfra = can('infra.manage');

  const oltsQuery = useQuery({
    queryKey: ['olts', activeOrg?.id ?? 'all'],
    queryFn: () => api.get<Page<Olt>>('/olts'),
    enabled: canReadFleet,
    refetchInterval: 60_000,
  });
  const reportsQuery = useQuery({
    queryKey: ['reports', 'open'],
    queryFn: () => api.get<Page<Report>>('/reports', { status: 'open', limit: 6 }),
    enabled: canReadAudit,
  });
  const systemQuery = useQuery({
    queryKey: ['system-info'],
    queryFn: () => api.get<SystemInfo>('/settings/system'),
    enabled: canReadSettings,
    refetchInterval: 60_000,
  });
  // Automated sync/probe entries drown out what people actually did, so the
  // activity list asks for a wider slice and keeps only the human actions.
  const auditQuery = useQuery({
    queryKey: ['audit', 'recent'],
    queryFn: () => api.get<Page<AuditEntry>>('/audit', { limit: 40 }),
    enabled: canReadAudit,
  });

  // Unauthorised ONUs are read per OLT from the chassis, so this is deliberately
  // manual — it opens CLI sessions and must not run on a 60s dashboard timer.
  const unauthorisedQuery = useQuery({
    queryKey: ['unauthorised', activeOrg?.id ?? 'all'],
    queryFn: async () => {
      const list = await api.get<Page<Olt>>('/olts');
      const counts = await Promise.all(
        list.items
          .filter((o) => !o.disabledAt)
          .map((o) =>
          api
            .get<{ items: unknown[] }>(`/olts/${encodeURIComponent(o.id)}/unconfigured`)
            .then((r) => r.items.length)
            .catch(() => 0),
          ),
      );
      return counts.reduce((a, b) => a + b, 0);
    },
    enabled: canReadFleet,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  /**
   * The subscriber rows themselves — the same source the ONU page reads — so
   * the dashboard's breakdown and the ONU page's cards can never disagree.
   * One fetch a minute; the ONU page ships the identical payload on every
   * visit, so this adds nothing new to what the API already serves.
   */
  const onusQuery = useQuery({
    queryKey: ['onus', 'dashboard'],
    queryFn: () => api.get<Page<Onu>>('/onus'),
    enabled: canReadFleet,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  const olts = oltsQuery.data?.items ?? [];
  /**
   * Subscriber totals count the chassis SimpleACS is actually watching.
   *
   * A disabled OLT is not polled, so its ONU counts are frozen at whatever the
   * last sweep found. Rolled into the fleet total they would keep a
   * decommissioned chassis propping up the number on the dashboard for as long
   * as its rows exist, and "4,691 ONUs, 4,402 online" would be a reading of
   * something nobody is reading.
   */
  const watched = olts.filter((o) => !o.disabledAt);
  const onusTotal = watched.reduce((n, o) => n + o.onuCount, 0);
  const onusOnline = watched.reduce((n, o) => n + o.onuOnlineCount, 0);

  /**
   * One coloured segment of a breakdown line. The colours are the platform's
   * own vocabulary — ours green, another ACS the amber the row pills wear,
   * LOS the red a fibre break earns — and a ZERO stays faint on purpose: a
   * loud "0 LOS" is a call-out about nothing, while a quiet one still answers
   * the question. Same rendering as the ONU page's cards.
   */
  const seg = (n: number, label: string, cls: string, title?: string) => (
    <span key={label} title={title} className={n > 0 ? cls + ' font-semibold' : 'text-faint'}>
      {n.toLocaleString()} {label}
    </span>
  );
  const dot = (k: string) => (
    <span key={k} className="text-faint">
      ·
    </span>
  );

  /**
   * The two ONU-page breakdowns, combined into the one dashboard card: of the
   * online, how each is managed; of the offline, why, as far as the chassis
   * said. Same classification as the ONU page (management state where a
   * chassis read exists, the device link as the older fallback), same folding
   * of dying_gasp + power_off into "power cut".
   */
  const onuRows = onusQuery.data?.items ?? [];
  const onuBreakdown = (() => {
    if (onuRows.length === 0) return null;
    const mode = { tr069: 0, other: 0, omci: 0 };
    const cause = { power: 0, los: 0, unknown: 0 };
    let online = 0;
    let dormant = 0;
    for (const o of onuRows) {
      if (o.state === 'working') {
        online += 1;
        if (o.managementState === 'tr069') mode.tr069 += 1;
        else if (o.managementState === 'tr069_other') mode.other += 1;
        else if (o.managementState === 'omci') mode.omci += 1;
        else if (o.deviceId) mode.tr069 += 1;
        else mode.omci += 1;
      } else if (o.dormant) dormant += 1;
      // Dormant is split out of the outage causes: a long-gone unit is not a
      // power cut or a fibre fault to chase, so it is not counted as one.
      else if (o.state === 'dying_gasp' || o.state === 'power_off') cause.power += 1;
      else if (o.state === 'los') cause.los += 1;
      else cause.unknown += 1;
    }
    const offline = onuRows.length - online - dormant;
    return { online, offline, dormant, total: onuRows.length, mode, cause };
  })();

  /**
   * The chassis fleet in the same shape: EVERY chassis counted (a disabled
   * one is still a chassis), then live / unreachable / unknown among the ones
   * in service, and the ones a person took out of service — which are neither
   * up nor down, because nothing is asking them.
   */
  const oltBreakdown = (() => {
    const disabled = olts.filter((o) => o.disabledAt);
    const inService = olts.filter((o) => !o.disabledAt);
    const live = inService.filter((o) => o.status === 'up');
    const unreachable = inService.filter((o) => o.status === 'down');
    const unknown = inService.filter((o) => o.status !== 'up' && o.status !== 'down');
    return { disabled, live, unreachable, unknown };
  })();
  const openFindings = reportsQuery.data?.total ?? 0;
  const acsOk = systemQuery.data?.acs.ok ?? null;
  /**
   * A banner per OLT used to sit below these cards, one for every chassis
   * holding a `lastError`. It was removed because that field is not an error
   * log: a routine sweep writes "4 ONU state change(s) recorded" and "Read
   * over the CLI: 1361 ONUs across 32 ports" into it, so a completely healthy
   * fleet opened the dashboard with four orange warnings that never went away
   * and meant nothing. A warning that is always there is not a warning.
   *
   * What was worth keeping is the alarm underneath: an OLT that is actually
   * DOWN. That is the card below, which names them rather than leaving an
   * operator to work out which of four a "3 / 4" refers to. The per-ONU detail
   * lives on the OLTs page, in the Events column, where it can be read in
   * order and marked as seen.
   */
  // What people did, not what the scheduler did.
  const humanActivity = (auditQuery.data?.items ?? [])
    .filter((e) => !e.action.startsWith('olt.'))
    .slice(0, 4);

  return (
    <>
      <PageHeader
        title={`Welcome back, ${session?.user.name.split(' ')[0] ?? ''}`}
        description={
          activeOrg ? `Operating in ${activeOrg.name}.` : 'Platform-wide view across every organization.'
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Unauthorised ONUs"
          value={
            !canReadFleet ? '—' : unauthorisedQuery.isLoading ? '…' : (unauthorisedQuery.data ?? 0)
          }
          tone={(unauthorisedQuery.data ?? 0) > 0 ? 'warn' : 'success'}
          hint="waiting to be authorised on a PON port"
          icon={<TriangleAlert className="size-4" />}
        />
        <StatCard
          label="ONUs online"
          value={
            !canReadFleet
              ? '—'
              : onuBreakdown
                ? `${onuBreakdown.online.toLocaleString()} / ${onuBreakdown.total.toLocaleString()}`
                : `${onusOnline.toLocaleString()} / ${onusTotal.toLocaleString()}`
          }
          tone={onuBreakdown && onuBreakdown.offline === 0 ? 'success' : 'default'}
          hint={
            onuBreakdown ? (
              /* Both ONU-page breakdowns in one card: how the online are
                 managed, and why the offline are off. */
              <span className="grid gap-0.5 font-medium tabular-nums">
                <span className="flex flex-wrap items-baseline gap-x-1.5">
                  {seg(onuBreakdown.mode.tr069, 'TR-069', 'text-success')}
                  {dot('m1')}
                  {seg(onuBreakdown.mode.other, 'other ACS', 'text-warn')}
                  {dot('m2')}
                  {seg(onuBreakdown.mode.omci, 'OMCI', 'text-info')}
                </span>
                <span className="flex flex-wrap items-baseline gap-x-1.5">
                  {seg(onuBreakdown.cause.power, 'power cut', 'text-warn')}
                  {dot('c1')}
                  {seg(onuBreakdown.cause.los, 'LOS', 'text-danger')}
                  {dot('c2')}
                  {seg(onuBreakdown.cause.unknown, 'off unknown', 'text-muted')}
                  {dot('c3')}
                  {seg(onuBreakdown.dormant, 'dormant', 'text-muted')}
                </span>
              </span>
            ) : onusTotal > 0 ? (
              'reading the fleet…'
            ) : undefined
          }
          icon={<Radio className="size-4" />}
        />
        <StatCard
          label="OLTs"
          value={canReadFleet ? `${oltBreakdown.live.length} / ${olts.length}` : '—'}
          tone={
            olts.length === 0
              ? 'default'
              : oltBreakdown.unreachable.length > 0
                ? 'danger'
                : oltBreakdown.live.length + oltBreakdown.disabled.length === olts.length
                  ? 'success'
                  : 'warn'
          }
          hint={
            canReadFleet ? (
              /* Names ride the tooltip — four chassis fit a title, and the
                 line stays a line. */
              <span className="flex flex-wrap items-baseline gap-x-1.5 font-medium tabular-nums">
                {seg(oltBreakdown.live.length, 'live', 'text-success')}
                {dot('o1')}
                {seg(
                  oltBreakdown.unreachable.length,
                  'unreachable',
                  'text-danger',
                  oltBreakdown.unreachable.map((o) => o.name).join(', ') || undefined,
                )}
                {dot('o2')}
                {seg(
                  oltBreakdown.unknown.length,
                  'unknown',
                  'text-muted',
                  oltBreakdown.unknown.map((o) => o.name).join(', ') || undefined,
                )}
                {dot('o3')}
                {seg(
                  oltBreakdown.disabled.length,
                  'disabled',
                  'text-warn',
                  oltBreakdown.disabled.map((o) => o.name).join(', ') || undefined,
                )}
              </span>
            ) : undefined
          }
          icon={<Cable className="size-4" />}
        />
        <StatCard
          label="ACS"
          value={!canReadSettings ? '—' : acsOk == null ? '…' : acsOk ? 'Online' : 'Offline'}
          tone={acsOk == null ? 'default' : acsOk ? 'success' : 'danger'}
          hint={systemQuery.data && acsOk ? `${systemQuery.data.acs.latencyMs} ms` : undefined}
          icon={<Cloud className="size-4" />}
        />
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        {canManageInfra && <ServerResourcesCard />}

        {!canManageInfra && canReadFleet && (
          <Card
            title="OLT fleet"
            action={
              <Link to="/olts" className="text-xs text-link hover:underline">
                View all
              </Link>
            }
          >
            {oltsQuery.isLoading ? (
              <div className="flex justify-center p-8">
                <Spinner />
              </div>
            ) : olts.length === 0 ? (
              <EmptyState icon={<Cable className="size-8" />} title="No OLTs onboarded" />
            ) : (
              <Table>
                <THead>
                  <th className="th">Name</th>
                  <th className="th">Status</th>
                  <th className="th">ONUs online</th>
                  <th className="th">Last sync</th>
                </THead>
                <TBody>
                  {olts.map((olt) => (
                    <TRow key={olt.id}>
                      <td className="td">
                        <Link
                          to={`/olts/${olt.number ?? olt.id}`}
                          className="font-medium text-link hover:underline"
                        >
                          {olt.name}
                        </Link>
                        <span className="ml-2 font-mono text-xs text-faint">
                          {olt.vendor.toUpperCase()}
                        </span>
                      </td>
                      <td className="td">
                        <Pill state
                          tone={
                            olt.status === 'up' ? 'success' : olt.status === 'down' ? 'danger' : 'neutral'
                          }
                          dot
                        >
                          {olt.status}
                        </Pill>
                      </td>
                      <td className="td tabular-nums text-muted">
                        {olt.onuOnlineCount.toLocaleString()} / {olt.onuCount.toLocaleString()}
                      </td>
                      <td className="td text-muted">{formatRelative(olt.lastPolledAt)}</td>
                    </TRow>
                  ))}
                </TBody>
              </Table>
            )}
          </Card>
        )}

        {canReadAudit && (
          <Card
            title="Open findings"
            action={
              <Link to="/olt-log" className="text-xs text-link hover:underline">
                View all
              </Link>
            }
          >
            {reportsQuery.isLoading ? (
              <div className="flex justify-center p-8">
                <Spinner />
              </div>
            ) : (reportsQuery.data?.items.length ?? 0) === 0 ? (
              <EmptyState
                icon={<FileBarChart className="size-8" />}
                title="Nothing needs attention"
                description="Nightly drift, optical and restore checks have found nothing open."
              />
            ) : (
              <ul className="divide-y divide-line">
                {reportsQuery.data?.items.map((report) => (
                  <li key={report.id} className="flex items-start justify-between gap-4 px-5 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Pill tone="warn">{report.kind.replace(/_/g, ' ')}</Pill>
                        <span className="truncate font-mono text-xs text-muted">
                          {report.subject}
                        </span>
                      </div>
                      <p className="mt-1 truncate text-sm text-muted">{report.summary}</p>
                    </div>
                    <span className="shrink-0 text-xs text-faint">
                      {formatRelative(report.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
      </div>

      {canReadAudit && (
        <div className="mt-5">
          <Card
            title="Recent activity"
            action={
              <Link to="/audit" className="text-xs text-link hover:underline">
                View all
              </Link>
            }
          >
            {auditQuery.isLoading ? (
              <div className="flex justify-center p-8">
                <Spinner />
              </div>
            ) : humanActivity.length === 0 ? (
              <EmptyState title="Nothing has happened yet" />
            ) : (
              <ul className="divide-y divide-line">
                {humanActivity.map((entry) => (
                  <li key={entry.id} className="flex items-center justify-between gap-4 px-5 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-ink">{entry.action}</div>
                      <div className="truncate text-xs text-muted">{entry.actorLabel}</div>
                    </div>
                    <span className="shrink-0 text-xs text-faint">{formatRelative(entry.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

    </>
  );
}
